import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { newSceneGroupBaseSceneNote } from './base-scene-note';

import { ConversationTool } from './ConversationTool';
import { PAUSE_TAG_REGEX } from './pause-tags';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';

import { ApiService, LanguageEnum } from 'js/src/client';
import { getCanPublishAIAvatar, getCanUseAIAvatar } from 'js/src/data/account/account-selectors';
import {
    avatarBackgroundVideoAsset,
    avatarVideoAsset,
    fetchDefaultAIVoice,
    getAIAvatarById,
    getAIAvatarsOrm,
    getAIAvatarVoicePresetByAvatarId,
    setAIAvatars,
    setAIAvatarVoicePresets,
} from 'js/src/data/ai-voices-and-avatars';
import { getCurrentAIAvatar } from 'js/src/data/script-composer/script-composer-selectors';
import { generateVoiceoverAsset } from 'js/src/data/video-planner/video-planner-utils';
import type { RootState } from 'js/src/libs/RootState';
import {
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addAvatarSceneGroupSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new avatar scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
        ),
    transcript: z.string().describe('The narration text the avatar should speak'),
    avatar_id: z
        .number()
        .optional()
        .describe(
            `The id of the AI avatar to use. Use an id from the ${TOOL_NAMES.listAvatars} tool. Omit to use the default avatar.`,
        ),
    voice_slug: z
        .string()
        .optional()
        .describe(
            `Optional override for the avatar's voice. Defaults to the voice paired with the avatar. Use a slug returned by the ${TOOL_NAMES.searchVoices} tool.`,
        ),
    language_code: z
        .enum(
            Object.values(LanguageEnum).filter((lang) => lang !== LanguageEnum.XX) as [
                LanguageEnum,
                ...LanguageEnum[],
            ],
        )
        .optional()
        .describe('Language code for the voiceover. Defaults to "en"'),
});
export type AddAvatarSceneGroupArgs = z.infer<typeof addAvatarSceneGroupSchema>;

export class AddAIAvatarSceneGroupTool extends ConversationTool<typeof addAvatarSceneGroupSchema> {
    protected override readonly schema = addAvatarSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.addAvatarSceneGroup;
    protected override readonly toolDescription = `Add a new AI avatar scene group to the video at the specified index. An AI avatar is a generated on-screen presenter that speaks the provided transcript in an AI voice, with a synced transcript. Browse avatars with ${TOOL_NAMES.listAvatars} and pass the chosen \`avatar_id\`; omit it to use the default avatar. If you do not have a script, write one yourself from the user's request and any source material — do not ask the user to write it. Pause tags (\`<pause seconds="N"/>\`) can't be included in the transcript here — they'd be read aloud. Generate the avatar with the plain script, then add pauses with ${TOOL_NAMES.editAiVoiceover}.`;

    public override getToolDisplayName(): string {
        return 'Adding avatar scene group';
    }

    public get statusIcon(): string {
        return 'face';
    }

    public async execute(args: AddAvatarSceneGroupArgs, context: ToolExecutionContext) {
        if (!getCanUseAIAvatar(this.getState())) {
            return `Error: AI avatars are not available on this account.`;
        }

        const {
            after_scene_group_id,
            transcript,
            avatar_id,
            voice_slug,
            language_code = LanguageEnum.EN,
        } = args;
        const { parentActionGroup } = context;

        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        // Fresh RegExp avoids the sticky-lastIndex pitfall of the shared global-flagged regex.
        if (new RegExp(PAUSE_TAG_REGEX.source).test(transcript)) {
            return `Error: Pause tags (\`<pause seconds="N"/>\`) can't be included when generating an avatar — they'd be read aloud. Generate the avatar with the plain script, then add pauses with ${TOOL_NAMES.editAiVoiceover}.`;
        }

        try {
            // Avatars/voice presets are lazily loaded; fetch them if nothing has yet.
            if (getAIAvatarsOrm(this.getState()).isEmpty()) {
                const [avatarsResponse, presetsResponse] = await Promise.all([
                    ApiService.aiAvatarList(),
                    ApiService.activeAiAvatarVoicePresetList(),
                ]);
                this.dispatch(setAIAvatars(avatarsResponse.data));
                this.dispatch(setAIAvatarVoicePresets(presetsResponse.data));
            }

            const avatar =
                avatar_id !== undefined
                    ? getAIAvatarById(this.getState(), avatar_id)
                    : getCurrentAIAvatar(this.getState());
            if (!avatar) {
                return `Error: ${
                    avatar_id !== undefined
                        ? `No avatar found with id ${avatar_id}. Use the ${TOOL_NAMES.listAvatars} tool to see available avatars.`
                        : `No AI avatars are available for this account.`
                }`;
            }

            const preset = getAIAvatarVoicePresetByAvatarId(this.getState(), avatar.get('id'));

            let resolvedVoiceSlug = voice_slug ?? preset?.get('voice_slug');
            if (!resolvedVoiceSlug) {
                const defaultVoice = await this.dispatch(fetchDefaultAIVoice());
                if (!defaultVoice?.slug) {
                    return `Error: Could not determine a voice for the avatar. Use the ${TOOL_NAMES.searchVoices} tool to pick a voice and pass its slug as voice_slug.`;
                }
                resolvedVoiceSlug = defaultVoice.slug;
            }

            const { voiceoverAsset: audioAsset, transcript: voiceoverTranscript } =
                await generateVoiceoverAsset({
                    text: transcript,
                    voice: resolvedVoiceSlug,
                    languageCode: language_code,
                });

            const {
                createNewVoicedrivenSceneGroup,
                setAndApplyVoiceoverAssetForSceneGroup,
                deleteSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    setAndApplyVoiceoverAssetForSceneGroupOperation,
                    deleteSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_VOICEOVER,
                slot,
                {
                    deleteOtherScenes: false,
                    setPlaceholderAsset: false,
                    hasAvatar: true,
                },
                parentActionGroup,
                false,
            );

            // transcript is passed directly since a just-generated voiceover has no asset id yet.
            const hasAssetBeenApplied = await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData: audioAsset,
                avatarAssetData: avatarVideoAsset(avatar),
                avatarBackgroundAssetData: avatarBackgroundVideoAsset(preset),
                captionsLanguage: language_code,
                forceCaptionGeneration: false,
                selectedBlueprintId: null,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: null,
                recommendOverlays: false,
                isGlobalVideo: true,
                transcript: voiceoverTranscript,
                systemTriggered: false,
                parentActionGroup,
            });

            if (!hasAssetBeenApplied) {
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                return `Error: the avatar was generated but could not be applied to the new scene group.`;
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const durationSeconds = audioAsset.get('duration');
            const durationInfo =
                typeof durationSeconds === 'number'
                    ? ` Voiceover duration: ${durationSeconds.toFixed(1)}s.`
                    : '';

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';

            const publishNote = getCanPublishAIAvatar(this.getState())
                ? ''
                : ` Note: this team can preview avatar videos but cannot publish them — if the user asks to publish or download, call ${TOOL_NAMES.suggestUpgrade} with feature "ai_avatar" first.`;

            return `Success: Added new avatar scene group ${shortId} ${placement} with the "${avatar.get('name')}" avatar and its synced transcript.${durationInfo}${newSceneGroupBaseSceneNote()}${publishNote}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding avatar scene group: ${errorMessage}`;
        }
    }
}
